import React, { useEffect, useMemo, useRef, useState } from 'react';
import IntroduceTip from '@/components/IntroduceTip';
import pcStyles from './WordCloudCard.module.less';
import mobileStyles from './WordCloudCard.mobile.module.less';

type WordCloudSource =
  | string
  | string[]
  | Record<string, number | string>
  | { name?: string; word?: string; text?: string; value?: number; count?: number; weight?: number }[];

type NormalizedWord = {
  word: string;
  value: number;
};

type PlacedWord = NormalizedWord & {
  backgroundColor: string;
  color: string;
  depth: number;
  fontSize: number;
  fontWeight: number;
  gradient: string;
  height: number;
  opacity: number;
  scale3d: number;
  width: number;
  x: number;
  y: number;
  zIndex: number;
};

type WordCloudLayout = {
  height: number;
  words: PlacedWord[];
};

type WordRect = {
  height: number;
  width: number;
  x: number;
  y: number;
};

const DESKTOP_WORD_GAP = 7;
const MOBILE_WORD_GAP = 7;
const WORD_CLOUD_PERSPECTIVE = 720;
const WORD_CLOUD_SUBTITLE = '品牌问答中大模型对品牌的积极评价';

const getWordVisualStyle = (rankRatio: number, depth: number, index: number, isMobile: boolean, xRatio = 0.5) => {
  const isLeft = xRatio < 0.36;
  const isCenter = xRatio < 0.68;
  const coreGradient = isLeft
    ? 'linear-gradient(180deg, rgba(248, 178, 132, 0.52) 0%, rgba(235, 132, 83, 0.38) 100%)'
    : isCenter
      ? 'linear-gradient(180deg, rgba(248, 190, 139, 0.52) 0%, rgba(235, 151, 85, 0.38) 100%)'
      : 'linear-gradient(180deg, rgba(246, 210, 136, 0.52) 0%, rgba(229, 177, 83, 0.38) 100%)';
  const middleGradient = isLeft
    ? 'linear-gradient(180deg, rgba(250, 202, 171, 0.42) 0%, rgba(242, 159, 116, 0.3) 100%)'
    : isCenter
      ? 'linear-gradient(180deg, rgba(250, 209, 168, 0.42) 0%, rgba(241, 174, 112, 0.3) 100%)'
      : 'linear-gradient(180deg, rgba(248, 225, 165, 0.42) 0%, rgba(233, 194, 111, 0.3) 100%)';
  const outerGradient = isLeft
    ? 'linear-gradient(180deg, rgba(252, 222, 205, 0.34) 0%, rgba(244, 185, 149, 0.24) 100%)'
    : isCenter
      ? 'linear-gradient(180deg, rgba(252, 226, 196, 0.34) 0%, rgba(243, 198, 146, 0.24) 100%)'
      : 'linear-gradient(180deg, rgba(250, 234, 194, 0.34) 0%, rgba(237, 211, 145, 0.24) 100%)';

  if (isMobile) {
    if (index < 5 || rankRatio > 0.94) {
      return {
        backgroundColor: coreGradient,
        color: '#16171a',
        gradient: coreGradient,
      };
    }

    if (rankRatio > 0.72 || depth > 0.66) {
      return {
        backgroundColor: middleGradient,
        color: '#16171a',
        gradient: middleGradient,
      };
    }

    return {
      backgroundColor: outerGradient,
      color: '#16171a',
      gradient: outerGradient,
    };
  }

  if (rankRatio > 0.82 || depth > 0.78) {
    return {
      backgroundColor: coreGradient,
      color: '#16171a',
      gradient: coreGradient,
    };
  }

  if (rankRatio > 0.45 || depth > 0.46) {
    return {
      backgroundColor: middleGradient,
      color: '#16171a',
      gradient: middleGradient,
    };
  }

  return {
    backgroundColor: outerGradient,
    color: '#16171a',
    gradient: outerGradient,
  };
};

const getSeed = (text: string) => {
  let seed = 2166136261;

  for (let index = 0; index < text.length; index += 1) {
    seed ^= text.charCodeAt(index);
    seed = Math.imul(seed, 16777619);
  }

  return seed >>> 0;
};

const getRandom = (seed: number) => {
  const value = Math.sin(seed) * 10000;
  return value - Math.floor(value);
};

const estimateTextWidth = (word: string, fontSize: number) =>
  Array.from(word).reduce((width, char) => {
    if (/[\u4e00-\u9fa5]/.test(char)) {
      return width + fontSize * 1.04;
    }
    if (/[A-Z0-9]/.test(char)) {
      return width + fontSize * 0.72;
    }
    return width + fontSize * 0.64;
  }, 0);

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const isOverlapping = (
  rect: WordRect,
  placedRects: WordRect[],
  gap: number,
) =>
  placedRects.some(
    (item) =>
      Math.abs(rect.x - item.x) < (rect.width + item.width) / 2 + gap &&
      Math.abs(rect.y - item.y) < (rect.height + item.height) / 2 + gap,
  );

const getWordDepthOffset = (depth: number) => Math.round((depth * 56 - 18) * 10) / 10;

const getVisualScale = (depth: number, scale3d: number) => {
  const depthOffset = getWordDepthOffset(depth);
  const perspectiveScale = WORD_CLOUD_PERSPECTIVE / (WORD_CLOUD_PERSPECTIVE - depthOffset);

  return scale3d * perspectiveScale;
};

const getCollisionRect = ({ depth, height, scale3d, width, x, y }: PlacedWord): WordRect => ({
  height: height * getVisualScale(depth, scale3d),
  width: width * getVisualScale(depth, scale3d),
  x,
  y,
});

const isWithinBounds = (rect: WordRect, safeWidth: number, height: number) =>
  rect.x - rect.width / 2 >= 0 &&
  rect.x + rect.width / 2 <= safeWidth &&
  rect.y - rect.height / 2 >= 0 &&
  rect.y + rect.height / 2 <= height;

const getFontSize = (
  word: NormalizedWord,
  index: number,
  words: NormalizedWord[],
  isMobile: boolean,
  scale: number,
  shrink = 1,
) => {
  const values = words.map((item) => item.value);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const valueRatio = maxValue === minValue ? 1 : (word.value - minValue) / (maxValue - minValue);
  const rankRatio = 1 - index / Math.max(words.length - 1, 1);
  if (isMobile && index < 5) {
    const coreSizes = [16, 15.8, 15.6, 15.2, 14.8];
    return Math.round(coreSizes[index] * scale * shrink * 10) / 10;
  }

  const maxFontSize = isMobile ? 15.5 : 20;
  const minFontSize = isMobile ? 12 : 12;
  const finalValueRatio = maxValue === minValue ? rankRatio : valueRatio;
  const size = minFontSize + (maxFontSize - minFontSize) * (finalValueRatio * 0.72 + rankRatio * 0.28);

  return Math.round(size * scale * shrink * 10) / 10;
};

const getWordBox = (
  word: NormalizedWord,
  index: number,
  words: NormalizedWord[],
  isMobile: boolean,
  scale: number,
  shrink = 1,
) => {
  const fontSize = getFontSize(word, index, words, isMobile, scale, shrink);
  const width = Math.ceil(estimateTextWidth(word.word, fontSize) + (isMobile ? (index < 5 ? 54 : 20) : 30));
  const height = Math.ceil(fontSize * 1.2 + (isMobile ? (index < 5 ? 10 : 4) : 6));

  return {
    fontSize,
    height,
    width,
  };
};

const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
const centerSlots = [
  [0, 0],
  [-0.18, -0.12],
  [0.18, 0.1],
  [-0.08, 0.24],
  [0.1, -0.26],
];

const getSquareCandidatePoint = (
  index: number,
  attempt: number,
  seed: number,
  total: number,
): [number, number] => {
  if (attempt === 0 && centerSlots[index]) {
    const [x, y] = centerSlots[index];

    return [x + (getRandom(seed) - 0.5) * 0.04, y + (getRandom(seed + 17) - 0.5) * 0.04];
  }

  const rankProgress = index / Math.max(total - 1, 1);
  const baseRadius = index === 0 ? 0 : clamp(Math.sqrt(rankProgress) * 0.96, 0.1, 0.96);
  const angle =
    index * GOLDEN_ANGLE +
    getRandom(seed) * 0.42 +
    attempt * 0.71 +
    (getRandom(seed + attempt * 131) - 0.5) * 0.52;
  const squareScale = 1 / Math.max(Math.abs(Math.cos(angle)), Math.abs(Math.sin(angle)), 0.001);
  const radiusShift = attempt === 0 ? 0 : ((attempt % 7) - 3) * 0.045 + Math.floor(attempt / 7) * 0.018;
  const radius = clamp(baseRadius + radiusShift, index < 5 ? 0.02 : 0.08, 0.98);
  const offsetRange = index < 5 ? 0.06 : 0.12;
  const offsetX = (getRandom(seed + attempt * 97) - 0.5) * offsetRange;
  const offsetY = (getRandom(seed + attempt * 157) - 0.5) * offsetRange;

  return [
    clamp(Math.cos(angle) * squareScale * radius + offsetX, -0.98, 0.98),
    clamp(Math.sin(angle) * squareScale * radius + offsetY, -0.98, 0.98),
  ];
};

const getCenterOpacity = (x: number, y: number, centerX: number, centerY: number, radius: number, isMobile: boolean) => {
  const distance = Math.sqrt(((x - centerX) / radius) ** 2 + ((y - centerY) / radius) ** 2);

  if (distance < 0.16) {
    return 1;
  }

  return clamp(1 - distance * (isMobile ? 0.22 : 0.18), isMobile ? 0.62 : 0.58, 1);
};

const buildRadialLayout = (words: NormalizedWord[], containerWidth: number, isMobile: boolean): WordCloudLayout => {
  const safeWidth = Math.max(containerWidth, isMobile ? 260 : 640);
  const height = isMobile ? clamp(safeWidth * 0.58, 200, 224) : 204;

  if (!words.length) {
    return {
      height,
      words: [],
    };
  }

  const sortedWords = [...words].sort((a, b) => b.value - a.value);
  const placedWords: PlacedWord[] = [];
  const placedRects: WordRect[] = [];
  const gap = isMobile ? MOBILE_WORD_GAP : DESKTOP_WORD_GAP;
  const centerX = safeWidth / 2;
  const centerY = height / 2;
  const boundsPadding = isMobile ? 2 : 4;
  const layoutWidth = safeWidth;
  const opacityRadius = Math.min(layoutWidth, height) / 2;

  sortedWords.forEach((word, index) => {
    const seed = getSeed(`${word.word}-${index}`);
    const shrink = index > 0 ? clamp(1 - index * (isMobile ? 0.004 : 0.004), isMobile ? 0.82 : 0.78, 1) : 1;
    const scale = sortedWords.length > 40 ? clamp((isMobile ? 52 : 58) / sortedWords.length, isMobile ? 0.9 : 0.86, 1) : 1;
    const box = getWordBox(word, index, sortedWords, isMobile, scale, shrink);

    for (let attempt = 0; attempt < (isMobile ? 72 : 96); attempt += 1) {
      const [normalizedX, normalizedY] = getSquareCandidatePoint(index, attempt, seed, sortedWords.length);
      const x = centerX + normalizedX * (layoutWidth / 2 - box.width / 2 - boundsPadding);
      const y = centerY + normalizedY * (height / 2 - box.height / 2 - boundsPadding);
      const rect = { height: box.height, width: box.width, x, y };

      if (isWithinBounds(rect, safeWidth, height) && !isOverlapping(rect, placedRects, gap)) {
        const rankRatio = 1 - index / Math.max(sortedWords.length - 1, 1);
        const opacity = getCenterOpacity(x, y, centerX, centerY, opacityRadius, isMobile);
        const depth = clamp(opacity * 0.82 + rankRatio * 0.18, 0, 1);
        const visualStyle = getWordVisualStyle(rankRatio, depth, index, isMobile, x / safeWidth);
        const placedWord = {
          ...word,
          ...box,
          backgroundColor: visualStyle.backgroundColor,
          color: visualStyle.color,
          depth,
          fontWeight: 400,
          gradient: visualStyle.gradient,
          opacity,
          scale3d: (isMobile ? 0.99 : 0.95) + depth * (isMobile ? 0.01 : 0.06),
          x,
          y,
          zIndex: sortedWords.length - index,
        };
        const collisionRect = getCollisionRect(placedWord);

        if (!isWithinBounds(collisionRect, safeWidth, height) || isOverlapping(collisionRect, placedRects, gap)) {
          continue;
        }

        placedWords.push(placedWord);
        placedRects.push(collisionRect);
        break;
      }
    }
  });

  return {
    height,
    words: placedWords,
  };
};

interface IProps {
  isMobile?: boolean;
  title?: string;
  data?: WordCloudSource | null;
}

const normalizeWordCloud = (data?: WordCloudSource | null) => {
  if (!data) {
    return [];
  }

  if (typeof data === 'string') {
    return data
      .split(/[,，、\n]/)
      .map((word) => word.trim())
      .filter(Boolean)
      .map((word) => ({ word, value: 1 }));
  }

  if (Array.isArray(data)) {
    return data
      .map((item) => {
        if (typeof item === 'string') {
          return { word: item, value: 1 };
        }
        const word = item.name || item.word || item.text || '';
        const value = item.value ?? item.count ?? item.weight ?? 1;
        return { word, value: Number(value) || 1 };
      })
      .filter((item) => item.word);
  }

  return Object.entries(data)
    .map(([word, value]) => ({ word, value: Number(value) || 1 }))
    .filter((item) => item.word);
};

const WordCloudCard: React.FC<IProps> = ({ isMobile = false, title = '正面品牌词云', data }) => {
  const wordCloudRef = useRef<HTMLDivElement>(null);
  const [wordCloudWidth, setWordCloudWidth] = useState(0);
  const [isNarrowScreen, setIsNarrowScreen] = useState(false);
  const shouldUseMobileLayout = isMobile || isNarrowScreen;
  const styles = isMobile ? mobileStyles : pcStyles;
  const words = useMemo(() => normalizeWordCloud(data), [data]);
  const wordCloudLayout = useMemo(
    () => buildRadialLayout(words, wordCloudWidth, shouldUseMobileLayout),
    [shouldUseMobileLayout, wordCloudWidth, words],
  );

  useEffect(() => {
    const element = wordCloudRef.current;

    if (!element) {
      return undefined;
    }

    const updateWidth = () => setWordCloudWidth(Math.round(element.getBoundingClientRect().width));
    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const updateScreen = () => setIsNarrowScreen(window.innerWidth <= 1024);
    updateScreen();
    window.addEventListener('resize', updateScreen);

    return () => window.removeEventListener('resize', updateScreen);
  }, []);

  return (
    <section className={styles.card}>
      <div className={styles.titleWrap}>
        <div className={styles.title}>{title}</div>
        {!isMobile && <IntroduceTip title={WORD_CLOUD_SUBTITLE} />}
      </div>
      {isMobile && <div className={styles.subtitle}>{WORD_CLOUD_SUBTITLE}</div>}
      {words.length ? (
        <div
          className={styles.wordCloud}
          ref={wordCloudRef}
          style={{
            ['--word-cloud-height' as string]: `${wordCloudLayout.height}px`,
          }}
        >
          {wordCloudLayout.words.map((item, index) => (
            <span
              className={styles.word}
              data-word={item.word}
              key={`${item.word}-${index}`}
              style={{
                ['--word-bg' as string]: item.backgroundColor,
                ['--word-color' as string]: item.color,
                ['--word-depth' as string]: `${getWordDepthOffset(item.depth)}px`,
                ['--word-font-size' as string]: `${item.fontSize}px`,
                ['--word-font-weight' as string]: item.fontWeight,
                ['--word-gradient' as string]: item.gradient,
                ['--word-height' as string]: `${item.height}px`,
                ['--word-opacity' as string]: item.opacity,
                ['--word-scale-3d' as string]: item.scale3d,
                ['--word-width' as string]: `${item.width}px`,
                ['--word-x' as string]: `${item.x}px`,
                ['--word-y' as string]: `${item.y}px`,
                ['--word-z' as string]: item.zIndex,
              }}
            >
              {item.word}
            </span>
          ))}
        </div>
      ) : (
        <div className={styles.content}>
          <div className={styles.empty}>暂无数据</div>
        </div>
      )}
    </section>
  );
};

export default WordCloudCard;
